import { describeError, useApi } from '@classgo/app-kit';
import type { AnnouncementView, RecipientView } from '@classgo/shared';
import { Badge, Button, Card, EmptyState, Modal, Select, Table, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { announcementQueries, announcementsApi } from '../../announcements/api';
import { ConfirmDialog } from '../../components/confirm';
import { useT } from '../../i18n';
import { Composer } from './composer';

const tone = (status: AnnouncementView['status']) =>
  status === 'published'
    ? 'success'
    : status === 'scheduled'
      ? 'info'
      : status === 'archived'
        ? 'gray'
        : 'warning';

/**
 * The management side of announcements (ticket #71): the list with status and audience
 * filters, the composer, and per announcement the delivery status per channel and the read
 * receipts per recipient.
 */
export const AnnouncementsManage = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const [status, setStatus] = useState('');
  const [audience, setAudience] = useState('');
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState<AnnouncementView | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [archiving, setArchiving] = useState<AnnouncementView | null>(null);
  const list = useQuery(
    announcementQueries.list(api, status ? { status: status as AnnouncementView['status'] } : {}),
  );
  const rows = (list.data?.items ?? []).filter((row) => !audience || row.audience === audience);
  const refresh = () => queries.invalidateQueries({ queryKey: ['announcements'] });
  const publish = useMutation({
    mutationFn: (id: string) => announcementsApi(api).publish(id),
    onSuccess: () => {
      void refresh();
      toast.show({ tone: 'success', title: t('announcement.saved.published') });
    },
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('announcement.publish'),
        description: describeError(t, error),
      }),
  });
  return (
    <div className="space-y-4">
      <Card
        title={t('announcements.manage.title')}
        flush
        actions={<Button onClick={() => setComposing(true)}>{t('announcement.new')}</Button>}
      >
        <div className="grid gap-3 px-4 pt-4 sm:grid-cols-2">
          <Select
            label={t('announcements.filter.status')}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            options={[
              { value: '', label: t('students.filter.all') },
              ...(['draft', 'scheduled', 'published', 'archived'] as const).map((value) => ({
                value,
                label: t(`announcement.status.${value}`),
              })),
            ]}
          />
          <Select
            label={t('announcements.filter.audience')}
            value={audience}
            onChange={(event) => setAudience(event.target.value)}
            options={[
              { value: '', label: t('students.filter.all') },
              ...(['all', 'sede', 'grade', 'group', 'family'] as const).map((value) => ({
                value,
                label: t(`announcement.audience.${value}`),
              })),
            ]}
          />
        </div>
        {list.isError ? (
          <div className="p-6">
            <EmptyState
              title={t('entity.error.title')}
              description={describeError(t, list.error)}
            />
          </div>
        ) : (
          <Table<AnnouncementView>
            caption={t('announcements.manage.caption')}
            rows={rows}
            rowKey={(row) => row.id}
            loading={list.isPending}
            onRowClick={(row) => setSelected(row.id)}
            empty={
              <EmptyState
                title={t('announcements.manage.empty.title')}
                description={t('announcements.manage.empty.body')}
              />
            }
            columns={[
              {
                key: 'title',
                header: t('announcement.field.title'),
                cell: (row) => (
                  <div>
                    <p className="font-medium text-gray-900">{row.title}</p>
                    <p className="text-xs text-gray-500">
                      {t(`announcement.audience.${row.audience}`)}
                      {row.targetName ? ` · ${row.targetName}` : ''}
                    </p>
                  </div>
                ),
              },
              {
                key: 'status',
                header: t('announcements.filter.status'),
                cell: (row) => (
                  <Badge tone={tone(row.status)}>{t(`announcement.status.${row.status}`)}</Badge>
                ),
              },
              {
                key: 'when',
                header: t('announcements.column.when'),
                cell: (row) =>
                  row.publishedAt
                    ? new Date(row.publishedAt).toLocaleString('es-CO')
                    : row.scheduledAt
                      ? `${t('announcement.status.scheduled')} · ${new Date(row.scheduledAt).toLocaleString('es-CO')}`
                      : t('common.none'),
              },
              {
                key: 'reach',
                header: t('announcements.column.reach'),
                align: 'right',
                cell: (row) =>
                  row.status === 'published'
                    ? t('announcements.column.reach.value', {
                        read: row.recipients.read,
                        total: row.recipients.total,
                        emailed: row.recipients.emailed,
                      })
                    : t('common.none'),
              },
              {
                key: 'actions',
                header: '',
                cell: (row) => (
                  <div className="flex gap-2" onClick={(event) => event.stopPropagation()}>
                    {row.status === 'draft' || row.status === 'scheduled' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => publish.mutate(row.id)}
                        disabled={publish.isPending}
                      >
                        {t('announcement.publish')}
                      </Button>
                    ) : null}
                    {row.status !== 'archived' ? (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
                        {t('announcements.manage.edit')}
                      </Button>
                    ) : null}
                    {row.status !== 'archived' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger-700"
                        onClick={() => setArchiving(row)}
                      >
                        {t('announcement.archive')}
                      </Button>
                    ) : null}
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>
      {selected ? <Recipients id={selected} onClose={() => setSelected('')} /> : null}
      <Modal
        open={composing}
        onClose={() => setComposing(false)}
        title={t('announcement.new')}
        size="lg"
      >
        {composing ? <Composer current={null} onDone={() => setComposing(false)} /> : null}
      </Modal>
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t('announcement.edit')}
        size="lg"
      >
        {editing ? <Composer current={editing} onDone={() => setEditing(null)} /> : null}
      </Modal>
      <ConfirmDialog
        open={archiving !== null}
        title={t('announcement.archive')}
        body={t('announcement.archive.body', { title: archiving?.title ?? '' })}
        confirmLabel={t('announcement.archive')}
        cancelLabel={t('common.cancel')}
        onClose={() => setArchiving(null)}
        onConfirm={() => {
          if (!archiving) return;
          void announcementsApi(api)
            .archive(archiving.id)
            .then(() => {
              setArchiving(null);
              void refresh();
            });
        }}
      />
    </div>
  );
};

const Recipients = ({ id, onClose }: { id: string; onClose: () => void }) => {
  const t = useT();
  const api = useApi();
  const detail = useQuery(announcementQueries.detail(api, id));
  const recipients = useQuery(announcementQueries.recipients(api, id));
  return (
    <Card
      title={detail.data ? detail.data.title : t('common.loading')}
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      }
    >
      {detail.data ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <Badge tone={tone(detail.data.status)}>
            {t(`announcement.status.${detail.data.status}`)}
          </Badge>
          <span>
            {t('announcements.detail.reach', {
              total: detail.data.recipients.total,
              read: detail.data.recipients.read,
              emailed: detail.data.recipients.emailed,
            })}
          </span>
          {detail.data.attachments.length > 0 ? (
            <span>
              · {t('announcements.detail.attachments', { count: detail.data.attachments.length })}
            </span>
          ) : null}
        </div>
      ) : null}
      {recipients.isError ? (
        <p className="text-sm text-danger-700">{describeError(t, recipients.error)}</p>
      ) : (
        <Table<RecipientView>
          caption={t('announcements.detail.recipients')}
          rows={recipients.data?.items ?? []}
          rowKey={(row) => row.userId}
          loading={recipients.isPending}
          empty={<EmptyState title={t('announcements.detail.noRecipients')} description="" />}
          columns={[
            {
              key: 'name',
              header: t('announcements.recipients.name'),
              cell: (row) => (
                <div>
                  <p className="font-medium text-gray-900">{row.name}</p>
                  {row.studentName && row.studentName !== row.name ? (
                    <p className="text-xs text-gray-500">{row.studentName}</p>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'email',
              header: t('announcements.detail.email'),
              cell: (row) =>
                row.emailStatus === 'sent' ? (
                  <Badge tone="success">{t('announcements.delivery.sent')}</Badge>
                ) : row.emailStatus ? (
                  <Badge tone="warning">{row.emailStatus}</Badge>
                ) : row.email ? (
                  <Badge tone="gray">{t('announcements.delivery.pending')}</Badge>
                ) : (
                  <span className="text-xs text-gray-500">{t('invite.noEmail')}</span>
                ),
            },
            {
              key: 'read',
              header: t('announcements.detail.read'),
              cell: (row) =>
                row.readAt ? (
                  <Badge tone="success">{new Date(row.readAt).toLocaleString('es-CO')}</Badge>
                ) : (
                  <Badge tone="gray">{t('announcements.delivery.unread')}</Badge>
                ),
            },
          ]}
        />
      )}
    </Card>
  );
};
